import { Bot, Pencil, Plug, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { McpServer } from "@/api/types";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { StatCard } from "@/components/ui/stat-card";
import { ViewToggle, useViewMode } from "@/components/ui/view-toggle";
import { McpCard } from "@/features/mcps/components/McpCard";
import { McpFormDialog } from "@/features/mcps/components/McpFormDialog";
import { McpHealthBadge } from "@/features/mcps/components/McpHealthBadge";
import { useMcps } from "@/features/mcps/hooks/useMcps";
import { useMcpSummary } from "@/features/observability/hooks/useObservability";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { useAuth } from "@/auth/useAuth";
import { formatDate } from "@/lib/date";

export function McpListPage() {
  const { t } = useTranslation("common");
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<McpServer | null>(null);
  const [view, setView] = useViewMode("mcps:view", "cards");

  const { data, isLoading, isError, error, refetch } = useMcps({
    include_inactive: showInactive,
  });
  const { data: teams } = useTeams({ include_inactive: true });
  const summary = useMcpSummary();
  const { isAdmin, memberships } = useAuth();
  const canCreateSomewhere =
    isAdmin ||
    memberships.some((m) => m.role === "manager" || m.role === "creator");

  const teamMap = useMemo(
    () => new Map(teams?.map((tm) => [tm.id, tm.name]) ?? []),
    [teams],
  );

  const columns = useMemo<DataTableColumn<McpServer>[]>(
    () => [
      {
        key: "name",
        header: t("pages.mcps.columns.name"),
        render: (m) => (
          <span className="font-medium text-ink-950">{m.name}</span>
        ),
      },
      {
        key: "transport",
        header: t("pages.mcps.columns.transport"),
        render: (m) => m.transport.toUpperCase(),
      },
      {
        key: "team",
        header: t("pages.mcps.columns.team"),
        render: (m) => teamMap.get(m.owner_team_id) ?? "—",
      },
      {
        key: "health",
        header: t("pages.mcps.columns.health"),
        render: (m) => (
          <McpHealthBadge
            status={m.last_health_status}
            checkedAt={m.last_health_check_at}
          />
        ),
      },
      {
        key: "credentials",
        header: t("pages.mcps.columns.credentials"),
        render: (m) => (
          <Badge variant={m.has_credentials ? "secondary" : "outline"}>
            {m.has_credentials ? "Sim" : "Não"}
          </Badge>
        ),
      },
      {
        key: "updated",
        header: t("pages.mcps.columns.updated"),
        render: (m) => formatDate(m.updated_at),
      },
      {
        key: "actions",
        header: "",
        render: (m) =>
          canCreateSomewhere ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(m);
                setFormOpen(true);
              }}
            >
              <Pencil className="h-3 w-3" />
              {t("actions.edit")}
            </Button>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, teamMap, canCreateSomewhere],
  );

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("pages.mcps.title")}
        description={t("pages.mcps.description")}
        actions={
          <>
            <Button
              variant={showInactive ? "secondary" : "outline"}
              onClick={() => setShowInactive((v) => !v)}
            >
              {showInactive
                ? t("pages.llmModels.hideInactive")
                : t("pages.llmModels.showInactive")}
            </Button>
            <ViewToggle
              value={view}
              onChange={setView}
              labels={{
                cards: t("pages.mcps.viewCards"),
                list: t("pages.mcps.viewList"),
              }}
            />
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              disabled={!canCreateSomewhere}
            >
              <Plus className="h-4 w-4" />
              {t("pages.mcps.new")}
            </Button>
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <StatCard
          label={t("pages.mcps.summary.totalMcps")}
          value={summary.data?.total_mcp_servers.toLocaleString("pt-BR") ?? "—"}
          icon={Plug}
          loading={summary.isLoading}
        />
        <StatCard
          label={t("pages.mcps.summary.connectedAgents")}
          value={summary.data?.connected_agents.toLocaleString("pt-BR") ?? "—"}
          icon={Bot}
          loading={summary.isLoading}
        />
      </div>

      {data && data.length === 0 ? (
        <EmptyState
          title={t("pages.mcps.empty")}
          description={t("pages.mcps.emptyHint")}
        />
      ) : view === "list" ? (
        <DataTable columns={columns} rows={data ?? []} getRowKey={(m) => m.id} />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data?.map((mcp) => (
            <McpCard
              key={mcp.id}
              mcp={mcp}
              onEdit={() => {
                setEditing(mcp);
                setFormOpen(true);
              }}
            />
          ))}
        </ul>
      )}

      <McpFormDialog
        open={formOpen}
        onOpenChange={(v) => {
          setFormOpen(v);
          if (!v) setEditing(null);
        }}
        mcp={editing}
      />
    </div>
  );
}
